import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import Loader from '../reusable/Loader';
import TableSkeleton from '../reusable/TableSkeleton';
import Panel from '../reusable/Panel';
import useUserDetails from '../hooks/useUserDetails';
import { getConfiguredSchools, getEOAConfig } from '../../services/eoaConfigServices';
import { getQuoteSelectionWorkflowStatus } from '../../services/workflowService';
import { ConfiguredEOASchool } from '../../interfaces/EOAInterface';
import StudentVoiceStatus from './StudentVoiceStatus';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import { USER_ROLES } from '../../constants';
import './StudentVoiceForReportsOverview.css';
import { formatDate } from '../../utils';

interface SchoolWithStatus {
  id: string;
  name: string;
  status: string;
  dueDate: string;
}

const StudentVoiceForReportsOverview = () => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();

  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState<SchoolWithStatus[]>([]);

  useEffect(() => {
    const fetchSchoolsAndStatus = async () => {
      if (!userDetails) return;

      try {
        setLoading(true);

        const [eoaConfigResponse, schoolsResponse] = await Promise.all([
          getEOAConfig(userDetails.eoaId, userDetails.access_token),
          getConfiguredSchools(userDetails.eoaId, userDetails.access_token),
        ]);

        const sjwDate = eoaConfigResponse.data.sjw_date;

        const formattedDueDate = formatDate(sjwDate);

        const configuredSchools: ConfiguredEOASchool[] = schoolsResponse.data;
        const primarySchools = configuredSchools.filter((school) => school.is_primary_on_eoa !== false);

        const schoolsWithStatus = await Promise.all(
          primarySchools.map(async (school: ConfiguredEOASchool) => {
            let status: string = 'QUOTE_SELECTION_NOT_AVAILABLE';

            try {
              const statusResponse = await getQuoteSelectionWorkflowStatus(
                userDetails.eoaId,
                school.id,
                userDetails.access_token
              );
              status = statusResponse.current_state;
            } catch (error) {
              console.error(`Error fetching workflow status for school ${school.id}:`, error);
            }

            return {
              id: school.id,
              name: school.name,
              status,
              dueDate: formattedDueDate,
            };
          })
        );

        setSchools(schoolsWithStatus);
      } catch (error) {
        console.error('Error fetching schools and status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolsAndStatus();
  }, [userDetails]);

  const renderActionButton = (school: SchoolWithStatus) => {
    const userRole = userDetails?.role;
    const isLeadFacilitator = userRole === USER_ROLES.LEAD_FACILITATOR;
    const isLeadFacilitatorOrAdmin = userRole === USER_ROLES.XQ_ADMIN || userRole === USER_ROLES.LEAD_FACILITATOR;
    const isFacilitator = userRole === USER_ROLES.FACILITATOR;

    if (isLeadFacilitatorOrAdmin && school.status === 'QUOTE_SELECTION_COMPLETED') {
      return (
        <CustomButton
          variant="primary"
          size="small"
          buttonText="Review"
          buttonAction={() => navigate(`/student-voice-for-reports/${school.id}/review`)}
        />
      );
    }

    if (
      isFacilitator &&
      (school.status === 'QUOTE_SELECTION_IN_PROGRESS' || school.status === 'QUOTE_SELECTION_NOT_STARTED')
    ) {
      return (
        <CustomButton
          variant="primary"
          size="small"
          buttonText="Edit"
          buttonAction={() => navigate(`/student-voice-for-reports/${school.id}/review`)}
        />
      );
    }

    if (isLeadFacilitator && school.status === 'QUOTE_SELECTION_IN_PROGRESS') {
      return (
        <CustomButton
          variant="text"
          size="small"
          buttonText="View"
          buttonAction={() => navigate(`/student-voice-for-reports/${school.id}/review`)}
        />
      );
    }

    if (isFacilitator && school.status === 'QUOTE_SELECTION_COMPLETED') {
      return (
        <CustomButton
          variant="text"
          size="small"
          buttonText="View"
          buttonAction={() => navigate(`/student-voice-for-reports/${school.id}/review`)}
        />
      );
    }

    if ((isFacilitator || isLeadFacilitatorOrAdmin) && school.status === 'QUOTE_SELECTION_FINALIZED') {
      return (
        <CustomButton
          variant="text"
          size="small"
          buttonText="View"
          buttonAction={() => navigate(`/student-voice-for-reports/${school.id}/review`)}
        />
      );
    }

    if ((isFacilitator || isLeadFacilitatorOrAdmin) && school.status === 'QUOTE_SELECTION_NOT_AVAILABLE') {
      return (
        <CustomButton
          variant="text"
          size="small"
          buttonText="View"
          buttonAction={() => navigate(`/student-voice-for-reports/${school.id}/review`)}
        />
      );
    }

    return <span></span>;
  };

  const tableHeaderData: TableHeaderData<SchoolWithStatus>[] = [
    { key: 'name', label: 'School', icons: ['sort'] },
    { key: 'status', label: 'Status', icons: ['sort'] },
    { key: 'dueDate', label: 'Due date' },
    { key: 'actions', label: 'Actions' },
  ];

  if (!userDetails) return <Loader />;

  return (
    <div className="main-content">
      <CustomButton
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back" />}
        iconPosition="start"
        buttonAction={() => navigate('/overview')}
      />
      <div className="student-voice-header">
        <h1 className="student-voice-page-title">Student voice for reports</h1>
        <p className="student-voice-page-subtitle">
          Select student quotes and key themes from focus group sessions and surveys to feature in your school report.
        </p>
      </div>
      <Panel className="student-voice-overview-panel">
        <h2 className="student-voice-panel-title">Select a school</h2>
        <p className="student-voice-panel-description">
          Choose the school from which to review and select student quotes for EOAA reports.
        </p>
        <CustomTable
          tableHeaderData={tableHeaderData.map((header) => {
            if (header.key === 'status') {
              return {
                ...header,
                renderCell: (row: SchoolWithStatus) => <StudentVoiceStatus status={row.status} />,
              };
            }
            if (header.key === 'actions') {
              return {
                ...header,
                renderCell: (row: SchoolWithStatus) => renderActionButton(row),
              };
            }
            return header;
          })}
          tableBodyData={loading ? undefined : schools}
        >
          {loading && <TableSkeleton rowsNumber={3} colsNumber={tableHeaderData.length} />}
        </CustomTable>
      </Panel>
    </div>
  );
};

export default StudentVoiceForReportsOverview;
